<template>
    <PageWrapper :loading="isLoading" title="Single sign-on" :description="description">
        <template v-if="view && view.ssoEnabled" #headerActions>
            <NewButton
                data-test="cancel-rules"
                variant="outline"
                color="primary"
                :disabled="!isDirty || saving"
                @click="cancel"
            >
                Cancel
            </NewButton>
            <NewButton
                data-test="save-rules"
                variant="fill"
                color="primary"
                :loading="saving"
                :disabled="!isDirty || saving"
                @click="save"
            >
                Save
            </NewButton>
        </template>
        <p v-if="loadFailed" class="SsoProvisioningSettings__note">
            Single sign-on settings could not be loaded. Try again later.
        </p>
        <p v-else-if="view && !view.ssoEnabled" class="SsoProvisioningSettings__note">
            Single sign-on is not configured for this organization.
            Evident configures it; contact Evident to set it up.
        </p>
        <div v-else-if="view" class="SsoProvisioningSettings__content">
            <section class="SsoProvisioningSettings__section">
                <h3 class="SsoProvisioningSettings__title">
                    Connection
                </h3>
                <dl class="SsoProvisioningSettings__status">
                    <dt>Connection name</dt>
                    <dd data-test="sso-connection">
                        {{ view.connectionName || '-' }}
                    </dd>
                    <dt>Provider</dt>
                    <dd data-test="sso-provider">
                        {{ view.providerLabel || '-' }}
                    </dd>
                    <dt>Email domains</dt>
                    <dd data-test="sso-domains">
                        {{ domains.length ? domains.join(', ') : '-' }}
                    </dd>
                </dl>
                <p class="SsoProvisioningSettings__hint">
                    Anyone signing in with an email address at these domains is sent to this identity provider.
                </p>
            </section>
            <section class="SsoProvisioningSettings__section">
                <div class="SsoProvisioningSettings__toggle">
                    <SwitchToggle
                        :disabled="saving"
                        :value="draftEnabled"
                        hide-label
                        @input="setEnabled"
                    />
                    <!-- Guarded like the toggle it labels: without this the draft can be flipped
                         mid-save and the success snackbar lands on a toggle showing the opposite of
                         what the server now holds. -->
                    <span
                        class="SsoProvisioningSettings__toggleLabel"
                        :class="{ 'SsoProvisioningSettings__toggleLabel--disabled': saving }"
                        @click="saving || setEnabled(!draftEnabled)"
                    >
                        Dynamic user creation
                    </span>
                </div>
                <p class="SsoProvisioningSettings__hint" data-test="jit-helper">
                    When on, anyone signing in through the identity provider receives the role their groups map to,
                    and anyone matching no group receives no access.
                </p>
                <p v-if="!draftEnabled" class="SsoProvisioningSettings__warning" data-test="jit-off-warning">
                    While this is off, signing in no longer creates users or sets roles. People who have already
                    signed in keep working, at the role they had last time, and the group mapping below no longer
                    reaches them. Manage them on the Users &amp; Roles page instead — you can change anyone's role
                    there, and add new people there too, the same way you would without single sign-on.
                </p>
            </section>
            <section class="SsoProvisioningSettings__section">
                <h3 class="SsoProvisioningSettings__title">
                    Group mapping
                </h3>
                <p v-if="!draftEnabled" class="SsoProvisioningSettings__hint" data-test="mapping-inactive">
                    Not applied to anyone while dynamic user creation is off — roles are managed on the Users &amp;
                    Roles page instead. You can still set the mapping up now and turn it on after.
                </p>
                <GroupRulesTable
                    :rules="draft"
                    :row-errors="showRowErrors ? validation.rowErrors : []"
                    :protected-group-ids="protectedRows"
                    :disabled="saving"
                    @update:rules="onRulesChange"
                />
            </section>
        </div>
        <ConfirmationModal
            v-if="confirmingDisable"
            yes="Turn off dynamic user creation"
            yes-color="danger"
            no="Keep it on"
            title="Turn off dynamic user creation?"
            @input="resolveDisable"
            @close="resolveDisable(false)"
        >
            <p>
                No new users will be created when they sign in to <strong>{{ rpName }}</strong> through
                <strong>{{ view.connectionName }}</strong>. People who have already signed in keep working, at the
                role they hold today, and the group mapping stops updating it.
            </p>
            <p>
                You keep control from the Users &amp; Roles page: change anyone's role there, and add new people
                there as well. This is not a way to cut access in a hurry — it does not remove or reduce access for
                anyone who already has it.
            </p>
        </ConfirmationModal>
    </PageWrapper>
</template>

<style lang="scss">
    .SsoProvisioningSettings {
        &__content {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        &__warning {
            margin: 0;
            font-size: var(--text-body-size);
            color: var(--eidDangerRed);
        }

        &__section {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 20px;
            border: 1px solid var(--eidSecureBlue-25);
            border-radius: 8.75px;
            background-color: var(--eidWhite);
        }

        &__title {
            margin: 0;
            font-size: var(--text-body-size);
            font-weight: 600;
            color: var(--eidDarkBlue);
        }

        &__note,
        &__hint {
            margin: 0;
            font-size: var(--text-body-size);
            color: var(--eidTechGray);
        }

        &__status {
            display: grid;
            grid-template-columns: max-content 1fr;
            // Baselines, not box tops: the label is a step smaller than the value, so aligning the
            // boxes leaves the two sitting on visibly different lines.
            align-items: baseline;
            gap: 8px 24px;
            margin: 0;
            font-size: var(--text-body-size);

            dt {
                // A global rule puts margin-top on every dt after the first, which in a grid
                // offsets the label from its own value and stretches the row that holds them.
                margin: 0;
                color: var(--eidTechGray);
            }

            dd {
                margin: 0;
            }
        }

        &__toggle {
            display: flex;
            align-items: center;
            font-size: var(--text-body-size);

            .SwitchToggle {
                padding: 0;
                zoom: 0.9;
                margin-right: 15px;

                &--off .SwitchToggle__slider {
                    background-color: var(--eidLightGray);
                }

                &--on .SwitchToggle__slider {
                    background-color: var(--eidTrustGreen);
                }
            }
        }

        &__toggleLabel {
            cursor: pointer;
            user-select: none;

            &--disabled {
                cursor: default;
                opacity: 0.5;
            }
        }
    }
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import isEqual from 'lodash/isEqual';

    import { SwitchToggle } from '@evidentid/dashboard-commons/components/SwitchToggle';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { SsoGroupRuleView, SsoProvisioningView } from '@evidentid/tprm-portal-lib/models/sso-provisioning';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { usePageLayoutStore } from '@/modules/page-layout/store';
    import GroupRulesTable from '@/modules/sso-provisioning/components/GroupRulesTable.vue';
    import { useSsoProvisioningStore } from '@/modules/sso-provisioning/store';
    import { keepsOwnAdmin, protectedGroupIds } from '@/modules/sso-provisioning/utils/selfLockout';
    import { validateRules } from '@/modules/sso-provisioning/utils/validateRules';

    const copyRules = (rules: SsoGroupRuleView[]): SsoGroupRuleView[] => rules.map((rule) => ({ ...rule }));

    export default defineComponent({
        name: 'SsoProvisioningSettings',
        components: { ConfirmationModal, GroupRulesTable, NewButton, PageWrapper, SwitchToggle },
        data() {
            return {
                description: 'See how your organization signs in and map identity provider groups to portal roles.',
                draft: [] as SsoGroupRuleView[],
                // The toggle edits this, not the server. It only takes effect on Save, like the
                // mapping does -- turning it off stops provisioning anyone new and freezes everyone
                // else's access, which is too consequential to happen on a stray click.
                draftEnabled: false,
                saving: false,
                showRowErrors: false,
                // Set while the confirmation is open; holds the resolver that Save is awaiting.
                confirmingDisable: false,
                confirmResolver: null as ((confirmed: boolean) => void) | null,
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp.current;
            },
            // Never null: the stored null is folded into undefined so the template has one
            // "nothing to render" case. `storedView` keeps the distinction.
            view(): SsoProvisioningView | undefined {
                return useSsoProvisioningStore().view[this.rpName] ?? undefined;
            },
            // undefined = still loading, null = the load failed.
            storedView(): SsoProvisioningView | null | undefined {
                return useSsoProvisioningStore().view[this.rpName];
            },
            loadFailed(): boolean {
                return this.storedView === null;
            },
            isLoading(): boolean {
                return this.storedView === undefined;
            },
            validation() {
                return validateRules(this.draft);
            },
            rulesDirty(): boolean {
                return Boolean(this.view) && !isEqual(this.draft, this.view!.rules);
            },
            enabledDirty(): boolean {
                return Boolean(this.view) && this.draftEnabled !== this.view!.dynamicCreationEnabled;
            },
            isDirty(): boolean {
                return this.rulesDirty || this.enabledDirty;
            },
            // Defaulted, not read straight off the view: the two repos deploy independently, so a
            // backend that does not send `domainAliases` can serve this page. Reading
            // `view.domainAliases.length` there throws during render and blanks the whole page.
            domains(): string[] {
                return this.view?.domainAliases || [];
            },
            // Computed here rather than served by the API: the group claim never reaches
            // eid-insurance. See utils/selfLockout for why that is, and why doing it here is safe.
            protectedRows(): string[] {
                return protectedGroupIds(useSsoProvisioningStore().callerGroups, this.view?.rules || []);
            },
        },
        watch: {
            '$rp.current': {
                immediate: true,
                async handler(newVal: string, oldVal: string) {
                    if (!newVal) {
                        return;
                    }
                    // No `oldVal !== undefined` guard: the watcher is `immediate`, so on a deep
                    // link oldVal IS undefined and skipping here would mean the only time this page
                    // is reached without a client-side permission check is the one time nobody has
                    // performed one yet. Every other view in the portal checks on `newVal !== oldVal`
                    // alone.
                    if (newVal !== oldVal) {
                        await useRbacStore().validateRbac([ RbacFeature.configurationUsersAndRoles ]);
                    }
                    await useSsoProvisioningStore().load(newVal);
                },
            },
            // Saved rules (initial load, a reload, a successful save) become the editable draft.
            'view.rules': {
                immediate: true,
                handler(rules?: SsoGroupRuleView[]) {
                    this.draft = copyRules(rules || []);
                    this.showRowErrors = false;
                },
            },
            'view.dynamicCreationEnabled': {
                immediate: true,
                handler(enabled?: boolean) {
                    this.draftEnabled = Boolean(enabled);
                },
            },
        },
        created(): void {
            usePageLayoutStore().resetPageLayout();
            // Reads a claim off the token already in hand -- no network call, so nothing waits on it.
            useSsoProvisioningStore().loadCallerGroups();
        },
        methods: {
            onRulesChange(rules: SsoGroupRuleView[]): void {
                this.draft = rules;
            },
            // Discards every unsaved edit and puts the table back to what the server holds. Enabled
            // only while dirty, so it cannot be mistaken for a way to leave the page.
            cancel(): void {
                this.draft = copyRules(this.view?.rules || []);
                this.draftEnabled = Boolean(this.view?.dynamicCreationEnabled);
                this.showRowErrors = false;
            },
            // 🚨 Errors go to the snackbar, NOT inline. This page is long enough that an error
            // rendered beside the group table sits below the fold on a laptop, so a refused save
            // looks like nothing happened at all. The snackbar is the portal-wide convention for
            // this (40 call sites, including every user-management procedure) and it is anchored to
            // the viewport. Per-FIELD messages stay inline on the input they belong to -- those are
            // next to the input they describe, and `showRowErrors` still drives them.
            failed(message: string): void {
                useSnackbarStore().displaySnackbar({ message, success: false });
            },
            // The API answers a refused save with `{ message }`; show it as is. Takes the relying party
            // rather than reading `this.rpName`, because the caller is reporting a save that may have
            // been started against a different one.
            serverMessage(rpName: string, fallback: string): string {
                return useSsoProvisioningStore().saveError[rpName]?.message || fallback;
            },
            setEnabled(enabled: boolean): void {
                this.draftEnabled = enabled;
            },
            confirmDisable(): Promise<boolean> {
                this.confirmingDisable = true;
                return new Promise<boolean>((resolve) => {
                    this.confirmResolver = resolve;
                });
            },
            resolveDisable(confirmed: boolean): void {
                this.confirmingDisable = false;
                this.confirmResolver?.(confirmed);
                this.confirmResolver = null;
            },
            async save(): Promise<void> {
                this.showRowErrors = true;
                const { message } = this.validation;
                if (message) {
                    this.failed(message);
                    return;
                }
                // Last, after the shape checks: this one is about who is saving, not what. Refusing
                // here is the whole point -- the server's floor only guarantees SOMEBODY keeps
                // Admin, not that it is the person making the change.
                if (!keepsOwnAdmin(useSsoProvisioningStore().callerGroups, this.view?.rules || [], this.draft)) {
                    this.failed('This change would remove your own Admin access. Keep a group you belong to mapped to Admin.');
                    return;
                }
                // 🚨 EVERYTHING THIS SAVE ACTS ON IS READ BEFORE THE FIRST `await`. The relying-party
                // selector stays live while a save is in flight, and the watchers reset the draft when
                // it changes -- so re-reading `this.rpName` or `this.draftEnabled` afterwards would send
                // one tenant's draft to another, drop the pending toggle silently (`enabledDirty` reads
                // false against the new tenant) while still reporting success, and look up the failure
                // message under the wrong tenant.
                const rpName = this.rpName;
                const rules = copyRules(this.draft);
                const enabled = this.draftEnabled;
                const savingRules = this.rulesDirty;
                const savingEnabled = this.enabledDirty;
                // Turning it off stops provisioning and freezes existing access. That is not something to
                // discover from a toast after the fact.
                if (savingEnabled && !enabled && !(await this.confirmDisable())) {
                    return;
                }
                this.saving = true;
                const store = useSsoProvisioningStore();
                // Mapping first, switch second. Turning dynamic creation ON is what starts deciding
                // real logins, so it must never go live ahead of the rules it decides them by. The
                // reverse order would leave a window governed by the mapping being replaced.
                let ok = true;
                if (savingRules) {
                    ok = await store.saveRules(rpName, rules);
                }
                if (ok && savingEnabled) {
                    ok = await store.setEnabled(rpName, enabled);
                }
                this.saving = false;
                if (ok) {
                    useSnackbarStore().displaySnackbar({ message: 'Single sign-on settings saved.', success: true });
                } else {
                    this.failed(this.serverMessage(rpName, 'Saving the single sign-on settings failed.'));
                }
            },
        },
    });
</script>
